import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Stack,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { useDispatch, useSelector } from "src/redux/store";
import { getPlans } from "src/redux/slices/plan";
import { resolveFinalPrice, formatIvaCaption } from "src/utils/pricing";

// Selector visual de plan (Starter/Pro/Premium × mensual/anual).
// Usa el mismo lenguaje visual que AddOnsPicker/SupportPicker: tarjetas
// con icono + selección con cinta + hover elevado.
//
// El componente carga los planes del catálogo si aún no están en el
// store, hace matching por nombre (heurística "starter"/"pro"/"premium")
// contra 3 tarjetas visuales fijas, y filtra por ciclo según el toggle.
//
// Uso:
//   <PlanPicker
//     value={planId}       // int | null
//     onChange={setPlanId}
//     cycle={cycle}        // "monthly" | "annual"
//     onCycleChange={setCycle}
//   />
//
// Al cambiar de ciclo mantiene el tier — busca la variante mensual o
// anual del mismo tier y ajusta `value` automáticamente si existe.

const TIERS = [
  {
    key: "starter",
    fallbackName: "Starter",
    tagline: "Bar de barra, cafetería, panadería",
    icon: RocketLaunchRoundedIcon,
    color: "info",
    highlights: ["Mesas ilimitadas", "2 terminales", "TPV + carta + QR", "Control de jornada", "VeriFactu"],
  },
  {
    key: "pro",
    fallbackName: "Pro",
    tagline: "Restaurante con servicio en mesa",
    icon: StarRoundedIcon,
    color: "warning",
    recommended: true,
    highlights: [
      "Todo lo de Starter",
      "5 terminales",
      "Informes avanzados",
      "1 add-on gratis",
    ],
  },
  {
    key: "premium",
    fallbackName: "Premium",
    tagline: "El control total",
    icon: WorkspacePremiumRoundedIcon,
    color: "primary",
    highlights: ["Todo lo de Pro", "10 terminales", "Los 4 add-ons GRATIS", "Multi-local (próximamente)"],
  },
];

function tierOfName(name) {
  const n = (name || "").toLowerCase();
  if (n.includes("premium")) return "premium";
  if (n.includes("pro")) return "pro";
  if (n.includes("starter")) return "starter";
  return null;
}

export default function PlanPicker({ value, onChange, cycle, onCycleChange }) {
  const dispatch = useDispatch();
  const { plans } = useSelector((s) => s.plan);

  useEffect(() => {
    if (!plans || plans.length === 0) dispatch(getPlans(false));
  }, [dispatch, plans]);

  // Empareja cada tarjeta visual con la variante del catálogo del
  // ciclo actual. Si no existe la variante (ej. no hay "Pro Anual"),
  // la tarjeta se muestra deshabilitada.
  const cards = useMemo(() => {
    const list = plans || [];
    const isAnnual = cycle === "annual";
    return TIERS.map((tier) => {
      const candidates = list.filter((p) => tierOfName(p.name) === tier.key && p.isActive);
      const match = candidates.find((p) => (isAnnual ? p.isYearly : p.isMonthly));
      return { tier, plan: match };
    });
  }, [plans, cycle]);

  // 2026-08-26 · Quitado el useEffect que sincronizaba `value` al
  // cambiar `cycle` (buscaba la variante del tier en el nuevo ciclo y
  // llamaba `onChange(swap.id)` → el caller lo interpretaba como
  // "cambiar de plan" y abría el modal de contratación al vuelo).
  // El toggle es SOLO visual: cambia qué planes se muestran (mensuales
  // vs anuales). La selección real ocurre únicamente al hacer click
  // explícito en un plan concreto del grid → ahí sí dispara `onChange`
  // y el modal de contratación del ciclo correspondiente.

  return (
    <Box>
      {/* Toggle mes / año arriba del grid */}
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={cycle}
          exclusive
          size="small"
          onChange={(_, v) => v && onCycleChange(v)}
        >
          <ToggleButton value="monthly" sx={{ px: 2, fontWeight: 700 }}>
            Mensual
          </ToggleButton>
          <ToggleButton value="annual" sx={{ px: 2, fontWeight: 700 }}>
            Anual
            <Chip
              size="small"
              color="success"
              label="2 meses"
              sx={{ ml: 1, height: 18, fontSize: 10, fontWeight: 800 }}
            />
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Grid container spacing={2}>
        {cards.map(({ tier, plan }) => {
          const IconComp = tier.icon;
          const unavailable = !plan;
          const selected = plan && value === plan.id;
          return (
            <Grid item xs={12} md={4} key={tier.key}>
              <Card
                onClick={() => plan && onChange(plan.id)}
                sx={{
                  position: "relative",
                  cursor: unavailable ? "not-allowed" : "pointer",
                  borderRadius: 2,
                  border: "2px solid",
                  borderColor: selected
                    ? `${tier.color}.main`
                    : tier.recommended
                    ? `${tier.color}.light`
                    : "divider",
                  bgcolor: selected ? `${tier.color}.lighter` : "background.paper",
                  opacity: unavailable ? 0.55 : 1,
                  transition: "all 0.15s",
                  "&:hover": unavailable
                    ? undefined
                    : { borderColor: `${tier.color}.main`, transform: "translateY(-2px)" },
                  height: "100%",
                }}
              >
                {tier.recommended && !selected && (
                  <Chip
                    size="small"
                    color={tier.color}
                    label="Recomendado"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      height: 20,
                      fontSize: 10,
                      fontWeight: 800,
                    }}
                  />
                )}
                {selected && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: "0 auto 0 0",
                      width: 4,
                      bgcolor: `${tier.color}.main`,
                    }}
                  />
                )}
                <CardContent sx={{ pl: selected ? 2.5 : 2, p: 2.5, height: "100%", display: "flex", flexDirection: "column" }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 1.5,
                        bgcolor: selected ? `${tier.color}.main` : `${tier.color}.lighter`,
                        color: selected ? "#fff" : `${tier.color}.dark`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconComp fontSize="medium" />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                        {plan?.name || tier.fallbackName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {tier.tagline}
                      </Typography>
                    </Box>
                    {selected && (
                      <CheckCircleRoundedIcon sx={{ color: `${tier.color}.main` }} />
                    )}
                  </Stack>

                  <Box sx={{ mb: 1.5 }}>
                    {unavailable ? (
                      <Typography variant="caption" sx={{ fontStyle: "italic", color: "text.disabled" }}>
                        Sin variante {cycle === "annual" ? "anual" : "mensual"}
                      </Typography>
                    ) : (
                      <>
                        <Stack direction="row" alignItems="baseline" spacing={0.5}>
                          <Typography variant="h4" sx={{ fontWeight: 900 }}>
                            {resolveFinalPrice(plan).toFixed(2)}€
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            /{cycle === "annual" ? "año" : "mes"}
                          </Typography>
                        </Stack>
                        {formatIvaCaption(plan) && (
                          <Typography variant="caption" sx={{ display: "block", color: "text.disabled", fontSize: 11, mt: 0.25 }}>
                            {formatIvaCaption(plan)}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>

                  <Stack spacing={0.4} sx={{ flex: 1 }}>
                    {tier.highlights.map((h) => (
                      <Stack key={h} direction="row" spacing={0.75} alignItems="flex-start">
                        <CheckCircleRoundedIcon
                          sx={{ color: `${tier.color}.main`, fontSize: 14, mt: 0.3 }}
                        />
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {h}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

PlanPicker.propTypes = {
  /** Id del plan seleccionado (número) o null si aún no eligió. */
  value: PropTypes.number,
  /** (newPlanId: number) => void */
  onChange: PropTypes.func.isRequired,
  /** "monthly" | "annual" — controla qué variantes se muestran. */
  cycle: PropTypes.oneOf(["monthly", "annual"]).isRequired,
  /** (newCycle: "monthly" | "annual") => void */
  onCycleChange: PropTypes.func.isRequired,
};
